import {
    useEffect,
    useState,
} from 'react';
import {
    useInfiniteQuery,
    useQuery,
} from '@tanstack/react-query';

import * as ZonesService from '../services/ZonesService';
import {
    DnsZone,
    ZoneWriteCapabilities,
} from '../types';

// FNP-1191 — READ HALF: query hooks only. No mutation hooks and nothing that
// invalidates, because nothing here writes. When the CRUD ticket adds
// createZone/updateZone/deleteZone it invalidates `zonesKeys.lists()` on
// success and `zonesKeys.detail(...)` on update — the key factory below is
// shaped so it can, without restructuring these reads.
export const zonesKeys = {
    all: ['zones'] as const,
    lists: () => [...zonesKeys.all, 'list'] as const,
    list: (connectionId?: string) => [...zonesKeys.lists(), {connectionId}] as const,
    details: () => [...zonesKeys.all, 'detail'] as const,
    // ⚠️ BOTH parts are in the key, because both are in the identity. Keying a
    // zone on `id` alone would serve one connection's zone for another's when
    // the adapter mints colliding ids.
    detail: (connectionId: string, id: string) => [...zonesKeys.details(), connectionId, id] as const,
    capabilities: (connectionId: string) => [...zonesKeys.all, 'capabilities', connectionId] as const,
};

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown (§9 of the module checklist).
export const PAGE_SIZE = 25;

export const useZonesInfiniteQuery = (connectionId?: string) => {
    return useInfiniteQuery({
        // connectionId is part of the key: omit a narrowing parameter and
        // changing it silently serves the previous one's cache.
        queryKey: zonesKeys.list(connectionId),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            ZonesService.fetchRecords(connectionId, PAGE_SIZE, pageParam),
        // ⚠️ THE ONLY END-OF-RESULTS SIGNAL IS A NULL nextToken.
        //
        // `limit` applies BEFORE filtering on this platform, so a page that
        // comes back with ZERO items and a live token is NORMAL and every match
        // may still be behind it. This branches on the token and NEVER on
        // `items.length` — a `!lastPage.items.length` guard here would stop
        // paging on the first sparse page and silently hide the whole result.
        //
        // MUST return undefined: returning null or the same token makes React
        // Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 60_000,
    });
};

// ⚠️ connectionId is REQUIRED by the schema, so the query stays DISABLED until
// one is resolved rather than firing a request that is guaranteed to fail
// validation. See useZone / DetailPage for how it is resolved from the route.
export const useZoneQuery = (connectionId?: string, id?: string) => {
    return useQuery({
        queryKey: zonesKeys.detail(connectionId ?? '', id ?? ''),
        // NULL for "no such zone", never undefined — same React Query v5 rule
        // as the capability query below. `notFound` in useZone is derived from
        // this null, so the not-found and error states stay distinguishable.
        queryFn: async (): Promise<DnsZone | null> => {
            if (typeof connectionId === 'undefined' || connectionId === '') {
                throw new Error('connectionId is required parameter');
            }
            if (typeof id === 'undefined' || id === '') {
                throw new Error('id is required parameter');
            }
            return await ZonesService.fetch(connectionId, id) ?? null;
        },
        enabled: Boolean(connectionId) && Boolean(id),
        staleTime: 60_000,
    });
};

// The capability snapshot behind the write-control hiding decision (FNP-1181).
// Longer stale time than the zone reads: a credential's capabilities change
// when someone re-connects the provider, not while a table is being scrolled.
export const useZoneCapabilitiesQuery = (connectionId?: string) => {
    return useQuery({
        queryKey: zonesKeys.capabilities(connectionId ?? ''),
        // ⚠️ Returns NULL, never undefined, for "no snapshot".
        //
        // React Query v5 rejects an undefined resolution outright — it logs
        // "Query data cannot be undefined" and leaves the query in a state that
        // is neither success nor error. The service says undefined; that is
        // normalised to null exactly here, at the one boundary that cares.
        queryFn: async (): Promise<ZoneWriteCapabilities | null> => {
            if (typeof connectionId === 'undefined' || connectionId === '') {
                throw new Error('connectionId is required parameter');
            }
            return await ZonesService.fetchConnectionCapabilities(connectionId) ?? null;
        },
        enabled: Boolean(connectionId),
        staleTime: 300_000,
    });
};

// ─── MODULE-SCOPED FILTER STORE ───
//
// The filter button, the drawer and the list hook live in DIFFERENT components
// (§4 of the global-filter-store pattern). A local useState would give each its
// own copy and the filter would silently do nothing. Do not replace with one.
//
// Two fields, both CLIENT-SIDE over the loaded window:
//   provider   — 'ROUTE53' | 'CLOUDFLARE' | 'FAKE'
//   delegation — 'DELEGATED' | 'NOT_DELEGATED' | 'UNDETERMINED'
//
// ⚠️ `delegation` has THREE values, not two, and UNDETERMINED is a real
// selectable state. A two-value filter would make null zones unreachable
// through the UI while still counting against the footer — the collapse the
// schema explicitly warns against, reintroduced at the filter instead of the
// badge.
export type ZonesFilters = Record<string, string>;

let globalFilters: ZonesFilters = {};
const filterListeners: Set<(filters: ZonesFilters) => void> = new Set();

const notifyFilterChange = (filters: ZonesFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

// Test-only reset. The store outlives a component tree by design, which means
// it also outlives a test case — without this, a filter set in one test leaks
// into the next.
export const resetZonesFilters = () => {
    globalFilters = {};
    filterListeners.clear();
};

export const useZonesFilter = () => {
    const [filters, setFilters] = useState<ZonesFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: ZonesFilters) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        // A consumer mounting after a filter was applied must not start empty.
        setFilters(globalFilters);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    return {
        filters,
        handleSetFilters: (newFilters: ZonesFilters) => notifyFilterChange(newFilters),
        handleClearFilters: () => notifyFilterChange({}),
    };
};
